import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';

/* ========= helpers ========= */
const addTS = (url) => (url ? `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}` : '');
const safePick = (obj, key, altObj, altKey) =>
  (obj && obj[key]) || (altObj && altKey && altObj[altKey]) || '';

const POLL_INTERVAL_MS = 1200;      // “期待更新”阶段的轮询间隔
const POLL_TIMEOUT_MS  = 5 * 60e3;  // 最长监听 5 分钟

const SimulationDashboard = () => {
  /* ========= 表单参数 ========= */
  const [formValues, setFormValues] = useState({
    NA: '1.5',
    Lambda: '0.58',
    RefractiveIndex: '1.518',
    OTFscale_SigmaX: '1.7',
    OTFscale_SigmaY: '1.7',
    Pixelsize: '0.108',
    PSFsize: '128',
    nMed: '1.33',
    Photon_Range: '300,10000',
    Bg_Range: '1,50',
    Perlin_Bg_Range: '1,50',
    Num_file: '10',
    Videos_per_file: '1000',
    Frames: '30',
    Image_dims: '64',
    Max_particles: '20',
    Zernike_Magnitude: ['0', '0', '0'],
    Zernike_Phase: ['0', '0', '0'],
  });

  /* ========= 资源预览 ========= */
  const [psfImages, setPsfImages] = useState({
    psf_clean: '',
    psf_noise: '',
    pupil_mag: '',
    pupil_phase: '',
  });
  const [videoUrl, setVideoUrl] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  /* ========= 运行状态（UI用） ========= */
  const [runLoading, setRunLoading] = useState(false);
  const [runStatus, setRunStatus]   = useState('idle');
  const [runPulse, setRunPulse]     = useState(false);

  /* ========= 文件监听 ========= */
  const watchTimerRef = useRef(null);
  const watchDeadlineRef = useRef(0);
  const watchTargetsRef = useRef([]);
  const baselineMetaRef = useRef({});

  useEffect(() => () => stopWatching(), []);

  const stopWatching = () => {
    if (watchTimerRef.current) {
      clearTimeout(watchTimerRef.current);
      watchTimerRef.current = null;
    }
    watchDeadlineRef.current = 0;
    watchTargetsRef.current = [];
    baselineMetaRef.current = {};
  };

  const handleChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  // 仅 HEAD 获取元信息（不下载内容）
  const headMeta = async (url) => {
    try {
      if (!url) return { ok: false, lm: '', etag: '', len: '' };
      const res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
      return {
        ok: res.ok,
        lm: res.headers.get('last-modified') || '',
        etag: res.headers.get('etag') || '',
        len: res.headers.get('content-length') || '',
      };
    } catch {
      return { ok: false, lm: '', etag: '', len: '' };
    }
  };

  // 开始监听：一旦发现变化就刷新 UI 并停止
  const startWatching = async (targets, urlsMap) => {
    stopWatching();
    watchTargetsRef.current = targets.slice();
    watchDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;

    const baseline = {};
    for (const key of targets) {
      const url = key === 'video' ? urlsMap.video : urlsMap[key];
      baseline[key] = await headMeta(url);
    }
    baselineMetaRef.current = baseline;

    const tick = async () => {
      if (Date.now() > watchDeadlineRef.current) {
        stopWatching();
        return;
      }
      let changed = false;
      const freshUrls = {};

      for (const key of watchTargetsRef.current) {
        const url = key === 'video' ? urlsMap.video : urlsMap[key];
        const now = await headMeta(url);
        const before = baselineMetaRef.current[key] || { ok: false, lm: '', etag: '', len: '' };

        const diff =
          now.ok !== before.ok ||
          now.lm !== before.lm ||
          now.etag !== before.etag ||
          now.len !== before.len;

        if (diff) {
          changed = true;
          freshUrls[key] = addTS(url);
        }
      }

      if (changed) {
        setPsfImages((prev) => ({
          psf_clean: freshUrls.psf_clean || prev.psf_clean,
          psf_noise: freshUrls.psf_noise || prev.psf_noise,
          pupil_mag: freshUrls.pupil_mag || prev.pupil_mag,
          pupil_phase: freshUrls.pupil_phase || prev.pupil_phase,
        }));
        if (freshUrls.video) setVideoUrl(freshUrls.video);

        stopWatching();
        toast.success('✅ 新结果已生成并刷新显示');
        return;
      }

      watchTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    watchTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  };

  /* ========= 预览 ========= */
  const handlePreviewPSF = async () => {
    setLoadingPreview(true);
    try {
      const generateRes = await fetch('/api/preview-psf/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues),
      });
      const generateData = await generateRes.json();
      if (!generateRes.ok) throw new Error(generateData.error || 'preview_psf error');

      const viewRes = await fetch('/api/preview-psf-view/');
      const viewData = await viewRes.json();
      const urls = viewData.urls || {};

      setPsfImages({
        psf_clean: addTS(safePick(viewData, 'psf_clean', urls, 'psf_clean')),
        psf_noise: addTS(safePick(viewData, 'psf_noise', urls, 'psf_noise')),
        pupil_mag: addTS(safePick(viewData, 'pupil_mag', urls, 'pupil_mag')),
        pupil_phase: addTS(safePick(viewData, 'pupil_phase', urls, 'pupil_phase')),
      });
      setVideoUrl(addTS(viewData.video_url || urls.video || ''));

      await startWatching(
        ['psf_clean', 'psf_noise', 'pupil_mag', 'pupil_phase', 'video'],
        {
          psf_clean: urls.psf_clean || viewData.psf_clean || '',
          psf_noise: urls.psf_noise || viewData.psf_noise || '',
          pupil_mag: urls.pupil_mag || viewData.pupil_mag || '',
          pupil_phase: urls.pupil_phase || viewData.pupil_phase || '',
          video: urls.video || viewData.video_url || '',
        }
      );
    } catch (err) {
      console.error(err);
      toast.error(`预览失败：${err.message}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  // （函数保留，按钮已经移除）
  const handlePreviewVideo = async () => {
    try {
      const res = await fetch('/api/preview-video-view/');
      const data = await res.json();
      const urls = data.urls || {};
      const v = addTS(data.video_url || urls.video || '');
      if (!v) {
        toast.warn('暂无视频，请先生成预览');
        return;
      }
      setVideoUrl(v);
      await startWatching(['video'], { video: urls.video || data.video_url || '' });
    } catch (err) {
      console.error(err);
      toast.error('预览视频失败');
    }
  };

  /* ========= 参数提交 ========= */
  const handleSubmitToBackend = async () => {
    try {
      // 1. 把表单里的字符串数字转成 number
      const processed = JSON.parse(
        JSON.stringify(
          formValues,
          (k, v) =>
            Array.isArray(v)
              ? v
              : (!isNaN(v) && v !== '' ? Number(v) : v)
        )
      );

      // 2. 读取当前用户信息（尽量避免 anonymous）
      let userData = null;
      try {
        userData =
          JSON.parse(localStorage.getItem('user')) ||
          JSON.parse(localStorage.getItem('authUser')) ||
          null;
      } catch (e) {
        userData = null;
      }

      const username =
        (userData && (userData.email || userData.username)) ||
        localStorage.getItem('user_email') ||
        localStorage.getItem('cognito_email') ||
        localStorage.getItem('username') ||
        'ymoye@purdue.edu';   // 最后兜底成你自己

      // 3. 算法名：从 SPTComparison.jsx 写入的 localStorage 里拿
      const selectedAlgorithm =
        localStorage.getItem('selectedAlgorithm') || 'SPTNet_v1';

      // 4. 发送给后端的 payload（只加 username + algorithm + sim_params）
      const payload = {
        username,
        algorithm: selectedAlgorithm,
        sim_params: processed,
        training_params: {},   // 这里先传空对象，后端会忽略或将来可扩展
      };

      console.log('📤 提交到 /api/params/save/ payload =', payload);

      const response = await axios.post('/api/params/save/', payload);

      if (response.status === 200) {
        toast.success('✅ 参数成功提交并保存到 S3（已创建 job）！');
        console.log('✅ 后端返回：', response.data);
      } else {
        console.error('后端非 200 响应：', response);
        toast.error('⚠️ 后端响应异常');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('❌ 提交失败，请检查后端服务或网络连接');
    }
  };

  /* ========= 运行状态轮询（暂时未挂按钮） ========= */
  const pollRunStatus = async () => {
    let done = false;
    while (!done) {
      try {
        const r = await fetch('/api/run_status/');
        const s = await r.json();
        if (s?.status) setRunStatus(s.status);
        done = s?.status === 'finished' || s?.status === 'idle' || s?.status === 'error';
        if (!done) await new Promise((res) => setTimeout(res, 3000));
      } catch {
        await new Promise((res) => setTimeout(res, 3000));
      }
    }
    if (runStatus === 'finished') toast.success('✅ 仿真完成');
  };

const handleRunSimulation = async () => {
  try {
    setRunLoading(true);
    setRunPulse(true);

    // ① 取本地保存的 last_sim_job
    const lastJobStr = localStorage.getItem("last_sim_job");
    console.log("last_sim_job =", lastJobStr);

    if (!lastJobStr) {
      toast.error("❌ 没找到 simulation job，请先点击上面的 Submit Simulation Parameters");
      return;
    }

    let jobId: string | null = null;
    let paramsS3Uri: string | null = null;

    try {
      const raw = JSON.parse(lastJobStr);

      // 兼容两种情况：
      // 1) 直接是 payload object: { job_id, config_s3_path, ... }
      // 2) tuple 数组: [statusCode, payload]
      let payload: any = raw;
      if (Array.isArray(raw) && raw.length === 2 && typeof raw[1] === "object") {
        payload = raw[1];
      }

      jobId = payload.job_id || payload.jobId || null;
      paramsS3Uri =
        payload.config_s3_path ||
        payload.params_s3_uri ||
        payload.sim_params_s3 ||
        null;
    } catch (e) {
      console.error("解析 last_sim_job 失败:", e);
      toast.error("❌ 本地 job 记录损坏，请重新提交 Simulation Parameters");
      return;
    }

    if (!jobId) {
      toast.error("❌ job_id 为空，请重新提交 Simulation Parameters");
      return;
    }

    // ② 拿 IdToken，带上 Authorization 头（要跟 debug_auth 一致）
    const idToken =
      localStorage.getItem("IdToken") ||
      localStorage.getItem("id_token") ||
      localStorage.getItem("access_token") ||
      "";

    if (!idToken) {
      toast.error("❌ 未检测到登录信息，请重新登录后再运行 Simulation");
      return;
    }

    const payload = { job_id: jobId };
    console.log("📨 调用 /api/jobs/run/ payload =", payload);

    const res = await axios.post("/api/jobs/run/", payload, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    console.log("✅ /api/jobs/run/ 返回：", res.data);
    toast.success("🚀 Simulation job 已提交到 SQS 队列！");

    if (paramsS3Uri) {
      console.log("使用的 sim_params.json 在 S3：", paramsS3Uri);
    }
  } catch (err) {
    console.error("启动 simulation job 失败:", err);
    toast.error("❌ 启动 simulation job 失败");
  } finally {
    setRunLoading(false);
    setRunPulse(false);
  }
};


  /* ========= 停止仿真 ========= */
  const handleStopSimulation = async () => {
    try {
      const res = await fetch('/api/stop_simulation/');
      const data = await res.json();
      toast.success(data.message || '✅ Simulation Stopped');
    } catch (err) {
      console.error(err);
      toast.error('❌ Failed to stop simulation');
    }
  };

  /* ========= 一键打包下载（JSZip） ========= */
  const handleDownloadAll = async () => {
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const filesToDownload = [
        { url: psfImages.psf_clean, name: 'psf_clean.png' },
        { url: psfImages.psf_noise, name: 'psf_noise.png' },
        { url: psfImages.pupil_mag, name: 'pupil_magnitude.png' },
        { url: psfImages.pupil_phase, name: 'pupil_phase.png' },
        { url: videoUrl, name: 'preview_video.mp4' },
      ].filter((f) => !!f.url);

      if (filesToDownload.length === 0) {
        toast.warn('暂无可下载的文件，请先生成预览');
        return;
      }

      for (const file of filesToDownload) {
        const res = await fetch(file.url);
        if (!res.ok) throw new Error(`${file.name}: ${res.status} ${res.statusText}`);
        const blob = await res.blob();
        zip.file(file.name, blob);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'visualization_results.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('✅ 已开始下载 ZIP');
    } catch (e) {
      console.error(e);
      toast.error(`下载失败：${e.message}`);
    }
  };

  /* ========= 占位图 ========= */
  const videoPlaceholder = process.env.PUBLIC_URL + '/placeholders/sample_00/video.mp4';

  /* ========= UI ========= */
  return (
    <div className="w-screen h-screen flex bg-white overflow-hidden">
      {/* 左侧参数输入区域 */}
      <div className="w-1/2 h-full flex justify-center items-center p-6">
        <div className="w-[90%] space-y-4">
          {/* PSF 参数 */}
          <div className="border p-4 rounded-xl shadow">
            <h2 className="font-bold mb-2 text-lg">PSF & Imaging setup</h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                'NA','Lambda','RefractiveIndex','OTFscale_SigmaX','OTFscale_SigmaY',
                'Pixelsize','PSFsize','nMed','Photon_Range','Bg_Range','Perlin_Bg_Range',
              ].map((field) => (
                <div key={field} className="flex justify-between items-center">
                  <label>{field}</label>
                  <input
                    name={field}
                    value={formValues[field]}
                    onChange={handleChange}
                    className="w-[60%] p-1 border border-gray-300 rounded"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Zernike */}
          <div className="border p-4 rounded-xl shadow">
            <h2 className="font-bold mb-2 text-lg">Zernike Coefficients</h2>
            <table className="w-full text-sm">
              <thead>
                <tr><th>Index</th><th>Phase</th><th>Magnitude</th></tr>
              </thead>
              <tbody>
                {[0, 1, 2].map((i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>
                      <input
                        type="text"
                        value={formValues.Zernike_Phase[i]}
                        onChange={(e) => {
                          const arr = [...formValues.Zernike_Phase];
                          arr[i] = e.target.value;
                          setFormValues({ ...formValues, Zernike_Phase: arr });
                        }}
                        className="w-full border rounded p-1"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={formValues.Zernike_Magnitude[i]}
                        onChange={(e) => {
                          const arr = [...formValues.Zernike_Magnitude];
                          arr[i] = e.target.value;
                          setFormValues({ ...formValues, Zernike_Magnitude: arr });
                        }}
                        className="w-full border rounded p-1"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex gap-2 mt-2">
              <button
                onClick={handlePreviewPSF}
                disabled={loadingPreview}
                className={`px-3 py-1 rounded text-white ${
                  loadingPreview ? 'bg-gray-400' : 'bg-blue-500'
                }`}
              >
                {loadingPreview ? 'Loading...' : 'Preview PSF'}
              </button>
            </div>
          </div>

          {/* Simulation 参数 */}
          <div className="border p-4 rounded-xl shadow">
            <h2 className="font-bold mb-2 text-lg">Simulation Params</h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {['Num_file', 'Videos_per_file', 'Frames', 'Image_dims', 'Max_particles'].map((field) => (
                <div key={field} className="flex justify-between items-center">
                  <label>{field}</label>
                  <input
                    name={field}
                    value={formValues[field]}
                    onChange={handleChange}
                    className="w-[60%] p-1 border border-gray-300 rounded"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 右侧图像 + 视频区域 */}
      <div className="w-1/2 h-full p-4 flex flex-col justify-center">
        <div className="w-full h-full flex flex-col justify-center">
          <div className="w-[100%] space-y-4">
            {/* 提交到后端 */}
            <div className="border p-4 rounded-xl shadow">
              <h2 className="font-bold mb-2 text-lg">Simulation Parameters to AWS</h2>
              <div className="flex space-x-4 mt-4">
                <div className="mt-4">
                  <button
                    className="bg-yellow-500 text-white px-6 py-2 rounded shadow"
                    onClick={handleSubmitToBackend}
                  >
                    Submit Simulation Parameters
                  </button>
                </div>
              </div>
            </div>

            {/* 运行脚本 */}
            <div className="border p-4 rounded-xl shadow">
              <h2 className="font-bold mb-2 text-lg">Run Simulation Script</h2>
              <div className="flex space-x-4 mt-4">
                <button
                  className={
                    `bg-red-600 text-white px-6 py-2 rounded shadow mt-4 ` +
                    (runPulse ? 'ring-2 ring-green-400 ring-offset-2 animate-pulse ' : '') +
                    (runLoading ? 'opacity-80 cursor-not-allowed ' : '')
                  }
                  onClick={handleRunSimulation}
                  disabled={runLoading}
                >
                  {runLoading ? 'Starting…' : 'Run Simulation Script'}
                </button>

                <button
                  className="bg-purple-600 text-white px-6 py-2 rounded shadow mt-4"
                  onClick={handleStopSimulation}
                >
                  Stop Simulation
                </button>

                <button
                  className="bg-blue-600 text-white px-6 py-2 rounded shadow mt-4"
                  onClick={() => (window.location.href = '/simulationdatavisualize')}
                >
                  Visualize simulation data
                </button>

                <button
                  className="bg-green-600 text-white px-6 py-2 rounded shadow mt-4"
                  onClick={() => (window.location.href = '/train')}
                >
                  Go to training page
                </button>
              </div>
              <div className="text-sm text-gray-600 mt-2">
                Status: <b>{runStatus}</b>
              </div>
            </div>

            {/* Visualization + Download */}
            <div className="border p-4 rounded-xl shadow relative">
              <div className="flex justify-between items-center mb-2">
                <h2 className="font-bold text-lg">Visualization</h2>
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-3 py-1 rounded"
                  onClick={handleDownloadAll}
                >
                  ⬇ Download
                </button>
              </div>

              <div className="flex gap-2 mt-3">
                {/* 四张图 */}
                <div className="flex-1 aspect-square p-2 bg-white rounded-xl shadow-sm flex flex-wrap gap-4 justify-center items-center">
                  {[
                    { key: 'psf_clean', title: 'Clean PSF' },
                    { key: 'psf_noise', title: 'Noisy PSF' },
                    { key: 'pupil_mag', title: 'Pupil Magnitude' },
                    { key: 'pupil_phase', title: 'Pupil Phase' },
                  ].map(({ key, title }) => (
                    <div
                      key={key}
                      className="w-[46%] aspect-square p-2 rounded-md border border-transparent shadow-none bg-transparent flex flex-col items-center justify-center"
                    >
                      <div className="text-xs font-semibold mb-1 text-center">{title}</div>
                      <img
                        src={
                          psfImages[key] ||
                          (process.env.PUBLIC_URL +
                            `/placeholders/sample_00/img_000${
                              key === 'psf_clean' ? '0' :
                              key === 'psf_noise' ? '1' :
                              key === 'pupil_mag' ? '2' : '3'
                            }.png`)
                        }
                        alt={title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>

                {/* 视频 */}
                <div className="flex-1 aspect-square p-2 rounded-md border border-transparent shadow-none bg-transparent flex flex-col items-center justify-center">
                  <div className="text-sm font-semibold mb-1 text-center">Video Preview</div>
                  <video
                    key={videoUrl || 'placeholder'}
                    src={videoUrl || videoPlaceholder}
                    controls
                    className="w-full h-full object-contain bg-black rounded"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default SimulationDashboard;
